import { ReactElement, useRef } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/_atoms/button";
import { Table, TableBody, TableHeader, TableRow } from "@/components/_atoms/table";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { MunicipalPropertyCatalogRow } from "@/domain/municipal-property/models/municipal-property-catalog-row.model";

import { useThresholdRowVirtualizer } from "../hooks/use-threshold-row-virtualizer";

import { MunicipalPropertyCatalogTableRow } from "./municipal-property-catalog-table-row";
import { MunicipalPropertyTableHead } from "./municipal-property-table-head";
import { MunicipalPropertyTableSpacerRow } from "./municipal-property-table-spacer-row";

interface MunicipalPropertyCatalogTableProps {
  propertyList: readonly MunicipalPropertyCatalogRow[];
  totalCount: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  onManage?: (property: MunicipalPropertyCatalogRow) => void;
}

const BASE_COLUMN_COUNT = 7;

export function MunicipalPropertyCatalogTable({
  propertyList,
  totalCount,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  onManage,
}: MunicipalPropertyCatalogTableProps) {
  const { t } = useDomainTranslation("municipal-property");
  const scrollElementRef = useRef<HTMLDivElement>(null);
  const { tableProps, headerRowProps, visibleRowList, topSpacerHeight, bottomSpacerHeight } =
    useThresholdRowVirtualizer({
      rowList: propertyList,
      totalCount,
      scrollElementRef,
    });
  const columnCount: number = BASE_COLUMN_COUNT + (onManage ? 1 : 0);

  return (
    <div ref={scrollElementRef} className="flex min-h-0 flex-1 flex-col overflow-auto">
      <Table containerClassName="overflow-visible" className="text-xs" {...tableProps}>
        <TableHeader className="[&_tr]:border-none">
          <TableRow {...headerRowProps}>
            <MunicipalPropertyTableHead>{t("catalog.columns.name")}</MunicipalPropertyTableHead>
            <MunicipalPropertyTableHead>{t("catalog.columns.type")}</MunicipalPropertyTableHead>
            <MunicipalPropertyTableHead>{t("catalog.columns.municipality")}</MunicipalPropertyTableHead>
            <MunicipalPropertyTableHead>{t("catalog.columns.address")}</MunicipalPropertyTableHead>
            <MunicipalPropertyTableHead className="text-right">
              {t("catalog.columns.usableFloorAreaM2")}
            </MunicipalPropertyTableHead>
            <MunicipalPropertyTableHead className="text-right">
              {t("catalog.columns.commissioningYear")}
            </MunicipalPropertyTableHead>
            <MunicipalPropertyTableHead>{t("catalog.columns.state")}</MunicipalPropertyTableHead>
            {onManage ? (
              <MunicipalPropertyTableHead className="text-right">
                {t("catalog.columns.actions")}
              </MunicipalPropertyTableHead>
            ) : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          <MunicipalPropertyTableSpacerRow height={topSpacerHeight} columnCount={columnCount} />
          {visibleRowList.map(
            ({ row: property, rowProps }): ReactElement => (
              <MunicipalPropertyCatalogTableRow
                key={property.id}
                property={property}
                rowProps={rowProps}
                onManage={onManage}
              />
            ),
          )}
          <MunicipalPropertyTableSpacerRow height={bottomSpacerHeight} columnCount={columnCount} />
        </TableBody>
      </Table>
      <div className="flex shrink-0 items-center justify-center gap-3 py-3">
        {hasNextPage ? (
          <Button variant="outline" size="xs" onClick={onLoadMore} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? (
              <Loader2 data-icon="inline-start" className="animate-spin" aria-hidden="true" />
            ) : null}
            {isFetchingNextPage ? t("catalog.loading") : t("catalog.loadMore")}
          </Button>
        ) : null}
        <span className="text-xs text-muted-foreground">
          {t("catalog.showing", {
            loaded: propertyList.length,
            total: totalCount,
          })}
        </span>
      </div>
    </div>
  );
}
